import { fetchEntities, createEntityWithDefaults } from "actions/entities";
import debounce from 'debounce'
import dotProp from 'dot-prop'
import pluralize from "pluralize";
import React from 'react'
import ReactDOM from 'react-dom'
import { connect } from 'react-redux'

import SelectorFieldControl from "./SelectorFieldControl";
import { getEntitiesForSelector, getEntityIdFromState, getEntityModel, mapEntitiesToSelectableOptions, withBlankOption } from "./selectorFieldOptions";

class PaginatedSelectorFieldControl extends React.Component {
  constructor(props) {
    super(props);
    this.state = { searchTerm: '' }
    this.dropdownRef = React.createRef()
    this.menuElement = null
    this.scrollThreshold = 24
    this.loadingMore = false
    this.debouncedSearch = debounce((searchTerm) => {
      this.props.fetchFirstPage({ search: searchTerm })
    }, 350)
  }

  componentDidMount() {
    this.props.fetchFirstPage()
  }

  componentWillUnmount() {
    this.debouncedSearch.clear()
    this.unbindMenuScroll()
  }

  render() {
    return (
      <SelectorFieldControl
        dropdownRef={this.dropdownRef}
        dropdownProps={{
          onSearchChange: this.handleSearchChange,
          onOpen: this.bindMenuScroll,
          onClose: this.unbindMenuScroll
        }}
        {...this.props}
      />
    )
  }


  handleSearchChange = (event, dropdownProps) => {
    const searchTerm = dropdownProps.searchQuery || ''
    this.setState({ searchTerm })
    this.debouncedSearch(searchTerm)
  }

  handleLoadMore = () => {
    if (this.loadingMore || !this.hasNextPage()) return

    const { activePage, totalPages } = this.props
    this.loadingMore = true
    const result = this.props.fetchNextPage(activePage, totalPages, {'search': this.state.searchTerm})
    Promise.resolve(result).finally(() => { this.loadingMore = false })
  }

  hasNextPage = () => {
    const { activePage, totalPages } = this.props
    return Boolean(activePage && totalPages && activePage < totalPages)
  }

  bindMenuScroll = () => {
    this.unbindMenuScroll()

    setTimeout(() => {
      const dropdownNode = this.getDropdownNode()
      if (!dropdownNode) return

      this.menuElement = dropdownNode.querySelector('.menu.visible') || dropdownNode.querySelector('.menu.transition.visible')
      if (this.menuElement) {
        this.menuElement.addEventListener('scroll', this.handleMenuScroll, { passive: true })
      }
    }, 0)
  }

  unbindMenuScroll = () => {
    if (this.menuElement) {
      this.menuElement.removeEventListener('scroll', this.handleMenuScroll)
      this.menuElement = null
    }
  }

  getDropdownNode = () => {
    if (!this.dropdownRef?.current) return null
    return ReactDOM.findDOMNode(this.dropdownRef.current)
  }

  handleMenuScroll = (event) => {
    const target = event?.target
    if (!target) return

    const reachedBottom = target.scrollTop + target.clientHeight >= target.scrollHeight - this.scrollThreshold
    if (reachedBottom) this.handleLoadMore()
  }
}

const ensureSelectedEntitiesIncluded = (entitiesInWindow, allEntities, entityId) => {
  let merged = entitiesInWindow
  const selectedIds = Array.isArray(entityId) ? entityId : [entityId]

  selectedIds.filter(Boolean).forEach((selectedId) => {
    if (!merged.some(entity => entity.id.toString() === selectedId.toString())) {
      const selectedEntity = allEntities.find(entity => entity.id.toString() === selectedId.toString())
      if (selectedEntity) merged = merged.concat(selectedEntity)
    }
  })

  return merged
}
const mapStateToProps = (state, ownProps) => {
  const options = ownProps.options
  const {
    entityName,
    displayAttribute,
    valueAttribute,
    includeBlank,
    EntityForm,
    selector,
    keyWindowName
  } = options

  let entityId = getEntityIdFromState(state, ownProps)
  const entityMetaName = pluralize.singular(entityName)
  const keyWindowIds = dotProp.get(state, `meta.${entityMetaName}.keyWindows.byWindow.${keyWindowName}.allIds`) || []
  const activePage = dotProp.get(state, `meta.${entityMetaName}.keyWindows.byWindow.${keyWindowName}.page`)
  const totalPages = dotProp.get(state, `meta.${entityMetaName}.keyWindows.byWindow.${keyWindowName}.totalPages`)
  const keyWindowIdsSet = new Set(keyWindowIds.map(id => id.toString()))

  const allEntities = getEntitiesForSelector(state, ownProps, { selector, entityName })
  const pagedEntities = allEntities.filter(entity => keyWindowIdsSet.has(entity.id.toString()))
  const entities = ensureSelectedEntitiesIncluded(pagedEntities, allEntities, entityId)
  let optionsToDisplay = entities.map(mapEntitiesToSelectableOptions(displayAttribute, valueAttribute))

  return {
    entityId,
    entityName,
    selectOptions: withBlankOption(optionsToDisplay, includeBlank, ownProps.translationScope),
    EntityForm,
    activePage,
    totalPages,
  }
}

const mapDispatchToProps = (dispatch, ownProps) => {
  const options = ownProps.options || {}
  const keyWindowName = options.keyWindowName
  const entityName = options.entityName
  const entitiesName = entityName ? pluralize.plural(entityName) : null
  const fetchEntityAfterSelect = options.fetchEntityAfterSelect

  return {
    handleChange: (event, dropdownProps) => {
      if (options.onSelect) dispatch(options.onSelect(dropdownProps.value))

      if (ownProps.updateValue) {
        ownProps.updateValue(event, dropdownProps)
      }

      if (fetchEntityAfterSelect) {
        dispatch(fetchEntities(entitiesName, { name: keyWindowName, filter: { id: dropdownProps.value }, page: 1, append: true }))
      }
    },
    fetchFirstPage: (filter = {}) => {
      dispatch(fetchEntities(entitiesName, { name: keyWindowName, filter: filter, page: 1 }))
    },
    fetchNextPage: (activePage, totalPages, filter = {}) => {
      if (activePage && totalPages && activePage < totalPages) {
        return dispatch(fetchEntities(entitiesName, { name: keyWindowName, filter: filter, page: activePage + 1, append: true }))
      }
      return Promise.resolve()
    },
    createEntityWithDefaults: () => {
      dispatch(createEntityWithDefaults(entityName))
    }
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(PaginatedSelectorFieldControl);
